import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getGrilleErdv, forcerCreneauErdv, getJournalErdv, recalculerErdv } from '../services/api';

/**
 * Créneaux E-RDV — la grille que la plateforme de Covage consulte.
 *
 * Ce n'est pas une copie locale : chaque geste écrit dans le module Zoho
 * « E-RDV COVAGE », relu après écriture pour que la page n'affiche jamais un
 * état qu'elle n'a pas vérifié.
 *
 * Un clic sur une pastille fait tourner les trois états : le moteur décide →
 * forcé ouvert → forcé fermé → le moteur décide. Un forçage n'est jamais
 * recalculé par le moteur : l'exploitation garde le dernier mot.
 */

const ETATS = {
  ouvert:  { fond: 'rgba(46,160,67,.85)',  texte: '#fff', bord: 'transparent', titre: 'Ouvert par le moteur' },
  ferme:   { fond: 'var(--bg3)',           texte: 'var(--text3)', bord: 'var(--border)', titre: 'Fermé — aucune capacité' },
  reserve: { fond: 'rgba(219,139,0,.9)',   texte: '#fff', bord: 'transparent', titre: 'Réservé par un client' },
  fantome: { fond: 'rgba(168,85,247,.85)', texte: '#fff', bord: 'transparent', titre: 'Fermé chez Covage, aucune commande reçue' },
  securite:{ fond: 'rgba(71,85,105,.95)',  texte: '#fff', bord: 'transparent', titre: 'Fermé par notre marge de sécurité' },
  collateral:{ fond: 'rgba(168,85,247,.38)', texte: '#fff', bord: 'rgba(168,85,247,.7)', titre: 'Fermé par la réservation d’un autre créneau' },
  force_o: { fond: 'rgba(46,160,67,.85)',  texte: '#fff', bord: '#ffd43b', titre: 'Forcé ouvert à la main' },
  force_f: { fond: 'var(--bg3)',           texte: '#ff6b6b', bord: '#ff6b6b', titre: 'Forcé fermé à la main' },
  absent:  { fond: 'transparent',          texte: 'var(--text3)', bord: 'transparent', titre: 'Pas de créneau dans la grille Zoho' },
};

function etatDe(c) {
  if (!c) return 'absent';
  if (c.reserve) {
    if ((c.commandes || []).length) return 'reserve';
    return c.ferme_par ? 'collateral' : 'fantome';
  }
  if (c.forcage) return c.forcage.valeur ? 'force_o' : 'force_f';
  if (!c.ouvert && /^marge de s/.test(String(c.motif || ''))) return 'securite';
  return c.ouvert ? 'ouvert' : 'ferme';
}

const SYMBOLE = { ouvert: '', ferme: '', reserve: '🔒', fantome: '?', collateral: '·', securite: '⌁', force_o: '★', force_f: '✕', absent: '·' };

function jourCourt(d) {
  const x = new Date(d + 'T12:00:00');
  return { jour: x.toLocaleDateString('fr-FR', { weekday: 'narrow' }), num: x.getDate() };
}

/** Un instant ISO en « lundi 12/10 à 17h42 », à l'heure de Paris. */
function horodate(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const jour = d.toLocaleDateString('fr-FR',
    { weekday: 'long', day: '2-digit', month: '2-digit', timeZone: 'Europe/Paris' });
  const heure = d.toLocaleTimeString('fr-FR',
    { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' }).replace(':', 'h');
  return `${jour} à ${heure}`;
}

/**
 * Tout ce qu'on sait d'une case, en lignes.
 *
 * L'infobulle du survol et le détail sous le tableau disaient deux choses
 * différentes : la première portait les commandes et les explications, le
 * second la seule date. Une case ne se raconte pas de deux façons — les deux
 * lisent désormais la même liste.
 */
function lignesDetail(dept, date, creneau, c, e) {
  const l = [`${dept} — ${date} à ${creneau}`, (ETATS[e] || {}).titre];

  // Zoho ne dit pas quand le client a réservé : la date est celle où notre
  // veille l'a vu, à deux minutes près. Les réservations antérieures au suivi
  // n'en ont pas, et le dire vaut mieux que de laisser croire à un oubli.
  if (e === 'reserve') {
    l.push(c && c.reserve_le && !Number.isNaN(Date.parse(c.reserve_le))
      ? 'Réservé le ' + horodate(c.reserve_le)
      : 'Réservé avant la mise en place du suivi');
  }

  // Nommer la commande : c'est ce qui distingue une vraie réservation d'un
  // créneau perdu pour rien.
  if (c && (c.commandes || []).length) {
    c.commandes.forEach(x => l.push('→ ' + x.ref + ' · ' + x.etape + (x.cp ? ' · ' + x.cp : '')
      + (x.client ? ' · ' + x.client : '')));
  }

  if (e === 'collateral' && c) {
    l.push('Fermé parce que le ' + c.ferme_par + ' du même jour a été réservé : le technicien '
      + 'parti à cette heure-là ne peut pas prendre celui-ci.');
    l.push('Aucune commande n’est à chercher.');
  }

  if (e === 'fantome') {
    l.push('Aucune commande ne nous est parvenue sur ce créneau.');
    if (c && c.reserve_initial) l.push('Fermé avant le démarrage du suivi, le ' + horodate(c.reserve_le) + '.');
    else if (c && c.reserve_le) l.push('Fermé le ' + horodate(c.reserve_le)
      + ' — la commande est peut-être en cours d’import.');
  }

  if (e === 'securite') {
    l.push('Fermé par nous : on garde un technicien en réserve plutôt que de promettre '
      + 'ce qu’on ne tiendra pas.');
  }

  if (c && c.motif) l.push(c.motif);

  if (c && c.forcage) {
    l.push('Forcé ' + (c.forcage.valeur ? 'ouvert' : 'fermé') + ' par ' + c.forcage.par
      + ' le ' + horodate(c.forcage.le)
      + (c.forcage.expire ? ', jusqu’au ' + c.forcage.expire : ''));
  } else if (c && c.le) {
    l.push('Dernière décision : ' + horodate(c.le) + (c.par ? ' (' + c.par + ')' : ''));
  }

  return l.filter(Boolean);
}

export default function ERDV() {
  const [data, setData]         = useState(null);
  const [jours, setJours]       = useState(15);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur]     = useState(null);
  const [message, setMessage]   = useState(null);
  const [occupe, setOccupe]     = useState(null);
  const [recalc, setRecalc]     = useState(false);
  const [journal, setJournal]   = useState([]);
  const [voirJournal, setVoirJournal] = useState(false);
  const [survol, setSurvol]     = useState(null);
  // La case sur laquelle on vient de cliquer, et où poser son menu.
  const [menu, setMenu]         = useState(null);
  // Vingt-six départements sur quinze jours font quinze cents cases : on ne
  // regarde jamais tout à la fois. Liste vide = tous, pour que la page s'ouvre
  // comme avant.
  const [deptsChoisis, setDeptsChoisis] = useState([]);

  const charger = useCallback(() => {
    setChargement(true);
    return getGrilleErdv(jours)
      .then(setData)
      .catch(e => setErreur(e.response?.data?.error || 'Chargement impossible'))
      .finally(() => setChargement(false));
  }, [jours]);

  useEffect(() => { charger(); }, [charger]);

  const chargerJournal = () => {
    getJournalErdv({ limite: 150 }).then(d => setJournal(d.entrees || [])).catch(() => {});
  };

  // Le clic fait tourner : moteur → forcé ouvert → forcé fermé → moteur.
  const ouvrirMenu = (dept, date, creneau, rect) => {
    const c = data.cases[`${dept}|${date}|${creneau}`];
    if (!c) return;
    if (c.reserve) {
      setMessage({ ton: 'info', texte: (c.commandes || []).length
        ? 'Ce créneau est réservé par un client (' + c.commandes.map(x => x.ref).join(', ')
          + ') : il ne peut pas être rouvert.'
        : 'Ce créneau est fermé chez Covage et aucune commande ne nous est parvenue. '
          + 'Nous ne pouvons pas le rouvrir — seul Covage le peut.' });
      return;
    }
    setMessage(null);
    setMenu({ dept, date, creneau, c, e: etatDe(c), x: rect.left, y: rect.bottom });
  };

  const poser = async (dept, date, creneau, suivant) => {
    const c = data.cases[`${dept}|${date}|${creneau}`];
    if (!c) return;
    setMenu(null);
    if (c.reserve) {
      setMessage({ ton: 'info', texte: (c.commandes || []).length
        ? 'Ce créneau est réservé par un client (' + c.commandes.map(x => x.ref).join(', ')
          + ') : il ne peut pas être rouvert.'
        : 'Ce créneau est fermé chez Covage et aucune commande ne nous est parvenue. '
          + 'Nous ne pouvons pas le rouvrir — seul Covage le peut.' });
      return;
    }
    setOccupe(`${dept}|${date}|${creneau}`);
    setMessage(null);
    try {
      const r = await forcerCreneauErdv({ dept, date, creneau, valeur: suivant });
      if (suivant === null) {
        setMessage({ ton: 'ok', texte: `Forçage retiré — le moteur a repris la main (${r.valeur_du_moteur ? 'ouvert' : 'fermé'}).` });
      } else if (r.ecrit_dans_zoho) {
        setMessage({ ton: 'ok', texte: `${dept} / ${date} ${creneau} → ${suivant ? 'ouvert' : 'fermé'} — écriture confirmée dans Zoho.` });
      } else {
        setMessage({ ton: 'ko', texte: 'Zoho n\'a pas pris l\'écriture — valeur relue : ' + String(r.valeur_relue_dans_zoho) });
      }
      await charger();
    } catch (err) {
      setMessage({ ton: 'ko', texte: err.response?.data?.error || 'Forçage impossible' });
    } finally { setOccupe(null); }
  };

  const relancer = async () => {
    setRecalc(true); setMessage(null);
    try {
      const r = await recalculerErdv({ jours });
      const s = r.resume;
      setMessage({ ton: 'ok', texte: `Moteur passé sur ${r.jours} jours ouvrés : ${s.ouvrir} ouverts, ${s.fermer} fermés, ${s.verrouilles} réservés, ${s.forces} forcés.` });
      await charger();
    } catch (e) {
      setMessage({ ton: 'ko', texte: e.response?.data?.error || 'Recalcul impossible' });
    } finally { setRecalc(false); }
  };

  // Les départements affichés, et les compteurs qui vont avec : un total qui
  // porterait sur toute la France alors qu'on ne regarde que le 95 ne dirait
  // rien d'utile.
  const deptsVisibles = useMemo(() => {
    if (!data) return [];
    return deptsChoisis.length
      ? data.departements.filter(d => deptsChoisis.includes(d))
      : data.departements;
  }, [data, deptsChoisis]);

  const stats = useMemo(() => {
    if (!data) return null;
    const s = { ouvert: 0, ferme: 0, reserve: 0, fantome: 0, collateral: 0, securite: 0, force_o: 0, force_f: 0, absent: 0 };
    deptsVisibles.forEach(d => data.jours.forEach(j => data.creneaux.forEach(cr => {
      s[etatDe(data.cases[`${d}|${j}|${cr}`])]++;
    })));
    return s;
  }, [data, deptsVisibles]);

  if (chargement && !data) return (
    <>
      <div className="topbar"><div className="topbar-title">Créneaux E-RDV</div></div>
      <div className="page"><div style={{ color: 'var(--text3)', padding: 40, textAlign: 'center' }}>Lecture de la grille Zoho…</div></div>
    </>
  );

  if (!data) return (
    <>
      <div className="topbar"><div className="topbar-title">Créneaux E-RDV</div></div>
      <div className="page"><div style={{ color: '#ff6b6b', padding: 40, textAlign: 'center' }}>{erreur}</div></div>
    </>
  );

  return (
    <>
      <div className="topbar">
        <div className="topbar-title">Créneaux E-RDV</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <select value={jours} onChange={e => setJours(parseInt(e.target.value, 10))}
            style={{ fontSize: 12, padding: '5px 8px', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 6, color: 'var(--text)' }}>
            {[10, 15, 20, 30, 45].map(n => <option key={n} value={n}>{n} jours ouvrés</option>)}
          </select>
          <button className="btn btn-sm" onClick={() => { setVoirJournal(v => !v); if (!voirJournal) chargerJournal(); }}>
            {voirJournal ? 'Masquer le journal' : 'Journal'}
          </button>
          <button className="btn btn-primary btn-sm" onClick={relancer} disabled={recalc}>
            {recalc ? 'Passage en cours…' : 'Relancer le moteur'}
          </button>
        </div>
      </div>

      <div className="page">
        <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14, lineHeight: 1.6, maxWidth: 860 }}>
          Cette grille <strong>est</strong> celle que la plateforme de Covage consulte : chaque clic écrit dans le
          module Zoho « E-RDV COVAGE » et la valeur est relue après écriture. Un clic fait tourner les états :
          le moteur décide → <span style={{ color: '#ffd43b' }}>★ forcé ouvert</span> →
          <span style={{ color: '#ff6b6b' }}> ✕ forcé fermé</span> → le moteur décide. Un forçage n'est jamais
          recalculé : vous gardez le dernier mot.
        </div>

        {/* Le filtre par département, avant la légende : il décide de ce que
            les compteurs comptent. */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 11, color: 'var(--text3)', marginRight: 3 }}>Départements</span>
          <button type="button" onClick={() => setDeptsChoisis([])}
            style={{
              fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6, cursor: 'pointer',
              border: '1px solid ' + (deptsChoisis.length ? 'var(--border)' : 'var(--accent-fonce, #2f81f7)'),
              background: deptsChoisis.length ? 'var(--bg3)' : 'rgba(47,129,247,.16)',
              color: 'var(--text)',
            }}>tous</button>
          {data.departements.map(d => {
            const actif = deptsChoisis.includes(d);
            return (
              <button key={d} type="button"
                onClick={() => setDeptsChoisis(l => actif ? l.filter(x => x !== d) : [...l, d])}
                style={{
                  fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 6, cursor: 'pointer',
                  border: '1px solid ' + (actif ? 'var(--accent-fonce, #2f81f7)' : 'var(--border)'),
                  background: actif ? 'rgba(47,129,247,.16)' : 'var(--bg3)',
                  color: 'var(--text)',
                }}>{d}</button>
            );
          })}
          {deptsChoisis.length > 0 && (
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>
              {deptsVisibles.length} sur {data.departements.length}
            </span>
          )}
        </div>

        {/* Légende + compteurs */}
        {stats && (
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 14, fontSize: 11 }}>
            {[['ouvert', 'ouverts'], ['reserve', 'réservés par un client'],
              ['fantome', 'fermés sans commande'], ['collateral', 'fermés par le créneau voisin'],
              ['securite', 'marge de sécurité'], ['ferme', 'fermés'],
              ['force_o', 'forcés ouverts'], ['force_f', 'forcés fermés'], ['absent', 'hors grille']].map(([k, lib]) => (
              <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{
                  width: 14, height: 14, borderRadius: 3, display: 'inline-block',
                  background: ETATS[k].fond, border: '1.5px solid ' + (ETATS[k].bord === 'transparent' ? ETATS[k].fond : ETATS[k].bord),
                }} />
                <span style={{ color: 'var(--text3)' }}>{stats[k]} {lib}</span>
              </span>
            ))}
          </div>
        )}

        {message && (
          <div style={{
            marginBottom: 14, padding: '9px 14px', borderRadius: 6, fontSize: 12,
            background: message.ton === 'ko' ? 'rgba(255,107,107,.12)' : message.ton === 'info' ? 'rgba(219,139,0,.12)' : 'rgba(46,160,67,.12)',
            border: '1px solid ' + (message.ton === 'ko' ? 'rgba(255,107,107,.4)' : message.ton === 'info' ? 'rgba(219,139,0,.4)' : 'rgba(46,160,67,.35)'),
            color: message.ton === 'ko' ? '#ff6b6b' : message.ton === 'info' ? '#d98a00' : 'var(--green2)',
          }}>{message.texte}</div>
        )}

        {/* Grille */}
        <div style={{ overflow: 'auto', paddingBottom: 8, maxHeight: 'calc(100vh - 250px)' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: '2px 3px', fontSize: 11 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', left: 0, top: 0, background: 'var(--bg)', zIndex: 3, textAlign: 'left', padding: '0 8px 0 0' }}></th>
                {data.jours.map(j => {
                  const { jour, num } = jourCourt(j);
                  return (
                    <th key={j} colSpan={data.creneaux.length} style={{ position: 'sticky', top: 0, zIndex: 2,
                      background: 'var(--bg)', padding: '0 0 4px', color: 'var(--text3)', fontWeight: 700 }}>
                      <div style={{ textTransform: 'uppercase' }}>{jour}</div>
                      <div style={{ fontSize: 13, color: 'var(--text2)' }}>{num}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {deptsVisibles.map(dept => (
                <tr key={dept}>
                  <td style={{
                    position: 'sticky', left: 0, background: 'var(--bg)', zIndex: 1,
                    padding: '0 10px 0 0', whiteSpace: 'nowrap',
                  }}>
                    <span style={{ fontSize: 13, fontWeight: 800 }}>{dept}</span>
                    {data.modes[dept] && data.modes[dept] !== 'auto' && (
                      <span style={{ fontSize: 9, color: 'var(--text3)', marginLeft: 5 }}>
                        {data.modes[dept] === 'rotation' ? 'rot.' : 'man.'}
                      </span>
                    )}
                  </td>
                  {data.jours.map(j => data.creneaux.map(cr => {
                    const cle = `${dept}|${j}|${cr}`;
                    const c = data.cases[cle];
                    const e = etatDe(c);
                    const st = ETATS[e];
                    const enCours = occupe === cle;
                    return (
                      <td key={cle} style={{ padding: 0 }}>
                        <div
                          onClick={ev => e !== 'absent'
                            && ouvrirMenu(dept, j, cr, ev.currentTarget.getBoundingClientRect())}
                          onMouseEnter={() => setSurvol({ dept, date: j, creneau: cr, c, e })}
                          onMouseLeave={() => setSurvol(null)}
                          // Plus d'infobulle : le bandeau sous le tableau dit la même
                          // chose, en plus lisible et sans attendre que la souris
                          // s'immobilise. L'étiquette reste pour qui navigue au
                          // clavier ou à la synthèse vocale.
                          aria-label={lignesDetail(dept, j, cr, c, e).join('. ')}
                          style={{
                            width: 17, height: 17, borderRadius: 3,
                            background: st.fond,
                            border: '1.5px solid ' + (st.bord === 'transparent' ? st.fond : st.bord),
                            color: st.texte, fontSize: 9, lineHeight: '14px', textAlign: 'center',
                            cursor: e === 'absent' ? 'default' : 'pointer',
                            opacity: enCours ? .4 : 1,
                          }}
                        >{SYMBOLE[e]}</div>
                      </td>
                    );
                  }))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6 }}>
          Trois pastilles par jour : {data.creneaux.join(', ')}. Le 16h est fermé définitivement — une
          intervention de quatre heures y finirait à 20 h.
        </div>

        {/* Le menu de la case cliquée. Trois choix nommés plutôt qu'un cycle
            aveugle : on voit ce qu'on va faire avant de le faire. */}
        {menu && (
          <>
            <div onClick={() => setMenu(null)}
              style={{ position: 'fixed', inset: 0, zIndex: 60 }} />
            <div style={{
              position: 'fixed', left: Math.min(menu.x, window.innerWidth - 270), top: menu.y + 6,
              zIndex: 61, width: 258, background: 'var(--bg2)', border: '1px solid var(--border)',
              borderRadius: 10, boxShadow: '0 12px 34px rgba(0,0,0,.35)', overflow: 'hidden',
            }}>
              <div style={{ padding: '9px 13px', borderBottom: '1px solid var(--border)' }}>
                <div style={{ fontSize: 12.5, fontWeight: 700 }}>
                  {menu.dept} — {menu.date} à {menu.creneau}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>
                  Actuellement : {(ETATS[menu.e] || {}).titre}
                </div>
              </div>
              {[
                [null,  'Laisser le moteur décider', 'Il rouvrira ou refermera selon la capacité.'],
                [true,  'Forcer ouvert',             'Le créneau reste offert à Covage quoi qu’il arrive.'],
                [false, 'Forcer fermé',              'Le créneau reste fermé quoi qu’il arrive.'],
              ].map(([valeur, titre, aide]) => {
                const actuel = (valeur === null && menu.e !== 'force_o' && menu.e !== 'force_f')
                  || (valeur === true && menu.e === 'force_o')
                  || (valeur === false && menu.e === 'force_f');
                return (
                  <button key={String(valeur)} type="button" disabled={actuel}
                    onClick={() => poser(menu.dept, menu.date, menu.creneau, valeur)}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left', border: 0,
                      background: 'none', font: 'inherit', padding: '9px 13px',
                      cursor: actuel ? 'default' : 'pointer',
                      color: actuel ? 'var(--text3)' : 'var(--text)',
                      borderTop: '1px solid var(--border)',
                    }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600 }}>
                      {titre}{actuel && ' · état actuel'}
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--text3)', lineHeight: 1.4 }}>{aide}</div>
                  </button>
                );
              })}
              <button type="button" onClick={() => setMenu(null)}
                style={{ display: 'block', width: '100%', textAlign: 'center', border: 0,
                         borderTop: '1px solid var(--border)', background: 'var(--bg3)',
                         font: 'inherit', fontSize: 11.5, padding: '7px 13px', cursor: 'pointer',
                         color: 'var(--text3)' }}>
                Annuler
              </button>
            </div>
          </>
        )}

        {/* Détail au survol — la même chose que l'infobulle, en plus lisible.
            Hauteur réservée : sans elle, le tableau sautait à chaque survol. */}
        <div style={{ marginTop: 14, minHeight: 92 }}>
          {survol && (
            <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)',
                          borderLeft: '4px solid ' + ((ETATS[survol.e] || {}).fond || 'var(--border)'),
                          borderRadius: 8, padding: '10px 14px', fontSize: 12, lineHeight: 1.55 }}>
              {lignesDetail(survol.dept, survol.date, survol.creneau, survol.c, survol.e)
                .map((ligne, i) => (
                  <div key={i} style={{
                    fontWeight: i === 0 ? 700 : i === 1 ? 600 : 400,
                    color: i <= 1 ? 'var(--text)' : 'var(--text2)',
                    marginTop: i ? 2 : 0,
                  }}>{ligne}</div>
                ))}
            </div>
          )}
        </div>

        {/* Journal */}
        {voirJournal && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Journal des décisions</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 10, maxWidth: 820 }}>
              Sans ce journal, personne ne pourrait expliquer à Covage pourquoi un créneau était ouvert ou non
              tel jour.
            </div>
            {journal.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)' }}>Journal vide.</div>
            ) : (
              <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8 }}>
                <thead>
                  <tr style={{ color: 'var(--text3)', textAlign: 'left' }}>
                    <th style={{ padding: '6px 10px' }}>Quand</th>
                    <th style={{ padding: '6px 10px' }}>Dept</th>
                    <th style={{ padding: '6px 10px' }}>Jour</th>
                    <th style={{ padding: '6px 10px' }}>Créneau</th>
                    <th style={{ padding: '6px 10px' }}>Action</th>
                    <th style={{ padding: '6px 10px' }}>Motif</th>
                  </tr>
                </thead>
                <tbody>
                  {journal.map((e, i) => (
                    <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '4px 10px', whiteSpace: 'nowrap', color: 'var(--text3)' }}>
                        {new Date(e.le).toLocaleString('fr-FR')}
                      </td>
                      <td style={{ padding: '4px 10px', fontWeight: 700 }}>{e.dept}</td>
                      <td style={{ padding: '4px 10px' }}>{e.date}</td>
                      <td style={{ padding: '4px 10px' }}>{e.creneau}</td>
                      <td style={{ padding: '4px 10px', color: e.action === 'ouvert' ? 'var(--green2)' : 'var(--text3)', fontWeight: 600 }}>
                        {e.action}
                      </td>
                      <td style={{ padding: '4px 10px', color: 'var(--text3)' }}>{e.motif}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </>
  );
}
